/**
 * The game family reducer — the only FULL reducer in M0.
 *
 * Its functional spec is PlayChess's socket callbacks (line refs below are
 * src/Screens/PlayChess/index.js in this worktree); its protocol spec is
 * cc-game-service (wire clocks are whole SECONDS, `moves` is a delta except
 * requestId 'on connect' which is full, `game_end_response` is re-sent until
 * `end_game_confirmed`, out-of-turn moves are silently dropped, illegal moves
 * come back as `invalid_move`).
 *
 * Rules for everything in this file:
 *  - pure: no React, no timers, no socket; ONE new state object returned;
 *  - `now` is injected so tests are deterministic;
 *  - the only side effects allowed are the named GamePlayStore bridge calls
 *    from `../gamePlayStoreWrites` (cross-screen mirror, PlayChess parity).
 */
import {
  bridgeResetGameBerserk,
  bridgeSetArenaStatus,
  bridgeSetPlaying,
  bridgeSetShowEndGameModal,
} from '../gamePlayStoreWrites';
import {mergeStatus} from './arena';
import {myFirstMovePly} from '../chess/firstMove';
import {classifyTimeControl} from '../chess/timeControl';
import {MODAL_DELAY_MS} from '../platform/modalDelay';
import {
  ArenaSlice,
  GameOrigin,
  GameRequest,
  GameResult,
  GameSlice,
  LiveGameState,
  START_FEN,
  Side,
  TournamentSlice,
  id,
  initialGame,
  initialWatch,
} from '../state';

/** requestIds that mark a restored snapshot rather than live play. */
const SNAPSHOT_ORIGINS = ['On reconnect', 'On fetch', 'On start'];

/** PlayChess L2956-3015 treats these endTypes as a draw. */
const DRAW_END_TYPES = ['draw', 'stalemate', 'insufficient', 'repetition'];

/** The types this reducer actually implements (see reducers/index.ts). */
export const GAME_TYPES = [
  'start_game_response',
  'join_game_response',
  'game_ready_response',
  'start_game',
  'player_turn_response',
  'player_move',
  'invalid_move',
  'draw_check',
  'draw_reject',
  'game_end_response',
  'no_ongoing_game',
  'tournament_opening_prep_eval_pending_response',
];

/** true when the message is for a different table than the one we're seated at. */
const otherTable = (game: GameSlice, data: any): boolean =>
  game.tableId !== null &&
  data?.tableId !== undefined &&
  Number(data.tableId) !== Number(game.tableId);

const withGame = (
  state: LiveGameState,
  game: Partial<GameSlice>,
): LiveGameState => ({...state, game: {...state.game, ...game}});

/** the two gamePlayModes a tournament surface spawns games with. */
const TOURNAMENT_MODES = ['tournament', 'arena'];

const isTournamentMode = (mode: any): boolean =>
  TOURNAMENT_MODES.includes(String(mode));

/**
 * Abort is a CASUAL-play affordance only. The old screen hides the button for
 * `gamePlayMode == 'tournament'` (L7177-7178, L7389-7390) and V2 hides it for
 * an ARENA too: an arena game is rated and paired by the server exactly like a
 * Swiss one, so a first-move abort there is the same unfair exit the tournament
 * rule exists to prevent (PARITY §9bis).
 */
const abortAllowed = (mode: any): boolean => !isTournamentMode(mode);

export function gameReducer(
  state: LiveGameState,
  type: string,
  data: any,
  now: number,
): LiveGameState {
  switch (type) {
    case 'start_game_response':
      return startGameResponse(state, data, now);
    case 'join_game_response':
      // PlayChess L2459-2463 only stores the tableId handed back by the
      // matchmaker; it is what `leave_table_request` cancels with.
      return {
        ...state,
        lobby: {...state.lobby, seekTableId: Number(data?.tableId) || 0},
      };
    case 'game_ready_response':
      return gameReadyResponse(state, data, now);
    case 'start_game':
      return startGame(state, now);
    case 'player_turn_response':
      return playerTurnResponse(state, data, now);
    case 'player_move':
      // Echo of a move already applied by player_turn_response. PlayChess
      // L2703-2709 only logs it. Meta is stamped by the root reducer.
      return state;
    case 'invalid_move':
      return invalidMove(state, data, now);
    case 'draw_check':
      return drawCheck(state, data);
    case 'draw_reject':
      return drawReject(state, data, now);
    case 'game_end_response':
      return gameEndResponse(state, data, now);
    case 'no_ongoing_game':
      return noOngoingGame(state);
    case 'tournament_opening_prep_eval_pending_response':
      return evalPending(state, data);
    default:
      return state;
  }
}

/**
 * start_game_response — PlayChess L2294-2406.
 * Sent live when a table spawns AND as the first half of the restore snapshot
 * ('On reconnect' / 'On fetch') and of the game start ('On start').
 */
function startGameResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  const mode = data?.gamePlayMode;
  const tournamentSpawn = isTournamentMode(mode);

  const tableId = Number(data?.tableId);
  const whiteId = id(data?.tossWinner); // tossWinner IS white (protocol)
  const playerList: string[] = Array.isArray(data?.playerList)
    ? data.playerList
    : [];
  const player1 = id(playerList[0]?.split(':')[0]);
  const player2 = id(playerList[1]?.split(':')[0]);
  const blackId = player1 === whiteId ? player2 : player1;
  const me = state.session.me;
  const opponentId = me && me === player1 ? player2 : player1;

  // The countdown is cosmetic; the SERVER owns the real deadline and sets the
  // FIRST_MOVE_TIMER per mode (cc-game-service processor/timerProcessor.js
  // ~L728): 'Play' = 20 s from the standard start, 60 s from a custom FEN;
  // 'tournament' = playerMoveTimer / 2; every other mode (arena, Play and
  // Learn, …) = 30 s. The old screens stored a clock THRESHOLD
  // (`total - 20`, L2328-2335) and displayed `clock - threshold`, i.e. the
  // same 20 s — V2 keeps the window LENGTH directly. (Owner bug 2026-10-05: a
  // 3-min game showed a 160 s window.)
  const totalTimeSec = Number(data?.playerMoveTimer || 0) / 1000;
  const firstMoveSec =
    mode === 'Play'
      ? data?.startFen === START_FEN
        ? 20
        : 60
      : mode === 'tournament'
      ? totalTimeSec / 2
      : 30;

  // L2336-2340: PlayChess applies the server-seeded touch piece 1.2 s later
  // (it waits for the board to mount). A reducer cannot hold a timer and the
  // V2 board reads `restrictedPiece` from the store, so it is applied now.
  let restrictedPiece: any = '';
  if (data?.touchPiece) {
    try {
      restrictedPiece = JSON.parse(data.touchPiece);
    } catch (e) {
      restrictedPiece = '';
    }
  }

  const origin: GameOrigin = SNAPSHOT_ORIGINS.includes(data?.requestId)
    ? (data.requestId as GameOrigin)
    : 'live';
  // Fix E1 (lifecycle row 25): 'On reconnect' / 'On fetch' hand back a game
  // ALREADY in progress — NOT 'On start', which is a genuinely new spawn — so
  // the first-move window must not be reset to a fresh `now + firstMoveSec`:
  // a network blip would otherwise gift a brand-new window (or resurrect one
  // the first move had already cleared). `firstMoveSec` itself is still
  // recomputed (it is just the server rule for this mode/TC, unchanged by a
  // reconnect) because `playerTurnResponse`'s own re-arm reads it back later.
  const isRestoredSnapshot =
    data?.requestId === 'On reconnect' || data?.requestId === 'On fetch';

  // Fix Q: what the user ASKED for, stamped from whatever was pending the
  // moment this table arrived (read BEFORE the lobby/challenge resets below
  // clear them). A tournament pairing and an accepted incoming challenge
  // leave both pending slots empty, so `request` is null for those — "New
  // game" then falls back to today's default (a fresh seek).
  //
  // A RESTORE of the SAME table keeps whatever was already stamped: 'On
  // reconnect' / 'On fetch' hand back the game this client is already
  // playing, and a reload mid-game must not cost the player the Play &
  // Learn / bot / library position that "New game" is supposed to replay.
  // A restore that hands back a DIFFERENT table is a game this client never
  // asked for here, so it stamps null like any other foreign start.
  const outgoingChallenge = state.challenge.outgoing;
  const pendingRequest: GameRequest | null =
    state.lobby.phase === 'seeking'
      ? {
          kind: 'seek',
          tcMs: state.lobby.tcMs,
          incMs: state.lobby.incMs,
          gamePlayMode: state.lobby.gamePlayMode,
        }
      : outgoingChallenge
      ? {
          kind: 'challenge',
          packet: {
            opponentId: outgoingChallenge.opponentId,
            opponentName: outgoingChallenge.opponentName,
            tcMs: outgoingChallenge.tcMs,
            incMs: outgoingChallenge.incMs,
            white: outgoingChallenge.white,
            fen: outgoingChallenge.fen,
            gamePlayMode: outgoingChallenge.gamePlayMode,
            variantId: outgoingChallenge.variantId,
          },
        }
      : null;
  const request: GameRequest | null =
    pendingRequest ??
    (isRestoredSnapshot && state.game.tableId === tableId
      ? state.game.request
      : null);

  const next: Partial<GameSlice> = {
    phase: 'starting',
    tableId,
    whiteId,
    blackId,
    myColor: me ? (me === whiteId ? 'white' : 'black') : null,
    opponentId,
    // L2402 sets myTurn from `username == white` before any turn message;
    // player_turn_response overwrites this within the same burst.
    turnUserId: whiteId,
    tcMs: Number(data?.playerMoveTimer || 0),
    incMs: Number(data?.moveTimeIncrement || 0),
    gamePlayMode: mode,
    assisted: mode === 'Play and Learn',
    restrictedPiece,
    firstMoveDeadlineMs: isRestoredSnapshot
      ? state.game.firstMoveDeadlineMs
      : now + firstMoveSec * 1000,
    firstMoveSec,
    origin,
    request,
    result: null,
    endAckNeeded: false,
    drawOffered: false,
    drawSent: false,
    drawRejectedAt: null,
  };

  // L2371-2390: only the restore snapshot re-seeds opponent/FEN/gameDetails —
  // in a live spawn those arrive on game_ready_response.
  if (isRestoredSnapshot) {
    const startFen = data?.startFen || START_FEN;
    next.opponentName = data?.opponentusername;
    next.opponentRating = Number(data?.opponentRating) || null;
    next.myRating = Number(data?.rating) || state.game.myRating;
    next.startFen = startFen;
    next.fen = state.game.tableId === tableId ? state.game.fen : startFen;
    next.isBlackTurnFirst = startFen.split(' ')[1] === 'b';
    next.gameDetails = buildGameDetails(data);
  }

  // Cross-screen mirror, PlayChess L2301-2305.
  bridgeSetPlaying(true);
  bridgeSetShowEndGameModal(false);

  return {
    ...withGame(state, next),
    lobby: {...state.lobby, phase: 'idle', playerNotFound: false},
    // A table spawned: whatever challenge was in flight is settled, and the
    // rail must not fall back to `challengeOut` when this game ends
    // (`selectRailState`). PlayChess does the same with
    // `setChallengeInProgress(false)` on its accept path.
    challenge: {...state.challenge, outgoing: null, notice: null},
    // L3539 setWatchGame(false): my own board wins over anything I was watching
    // — fix AC: a REPLAY too (`replayGameId` alone puts the rail in 'watch',
    // and a next-round / next-arena game must never spawn behind it).
    watch:
      state.watch.active || state.watch.replayGameId
        ? {...initialWatch}
        : state.watch,
    ...tournamentSpawnSlices(state, data, tournamentSpawn),
  };
}

/**
 * The tournament/arena half of a spawn — PlayChess L3589-3609.
 *
 * The old screen reacts to `gamePlayMode == 'tournament' | 'arena'` by adopting
 * the tournament context off the spawn (`setTournamentId`, `setRound`,
 * `setIsPairingGenerated(false)`) and, for an arena, clearing the previous
 * game's berserk badges and stamping `myState: 'paired'` OPTIMISTICALLY — the
 * server's 1:1 status response follows the spawn, and without the local stamp
 * the lobby card says "Finding your next game…" over a live board.
 *
 * `tournament.id` and `tournament.mode` are only ADOPTED, never overwritten:
 * the screen sets them from the details response
 * (`actions.setTournamentContext`) and that is the authoritative source for the
 * family; this is the reconnect path, where there was no details call.
 */
function tournamentSpawnSlices(
  state: LiveGameState,
  data: any,
  tournamentSpawn: boolean,
): Partial<LiveGameState> {
  if (!tournamentSpawn) {
    return {};
  }
  const mode = data?.gamePlayMode;
  const tournamentId = id(data?.tournamentId);
  // T2 review: the membership stamp below is scoped to the tournament the
  // client is ANCHORED on. A reconnect burst can deliver my live game from
  // tournament B while the screen shows A — `id` adoption already ignores the
  // mismatch, and `joinedLocal` must too, or A reads "joined" until the
  // context switches (a non-null `joinedLocal` outranks REST `is_joined` in
  // `statusInput.ts`). Same idea as `reducers/arena.ts`'s `sameArena` guard.
  const sameTournament =
    state.tournament.id === null || state.tournament.id === tournamentId;
  const tournament: TournamentSlice = {
    ...state.tournament,
    id: state.tournament.id || tournamentId || null,
    mode: state.tournament.mode || (mode === 'arena' ? 'arena' : 'swiss'),
    currentRound:
      data?.round === undefined
        ? state.tournament.currentRound
        : Number(data.round),
    pairingGenerated: false,
    // T2.1(b): membership is PROVEN by playing. `tournamentSpawn` already
    // means this is a tournament/arena game of MINE — `start_game_response`
    // has no watch variant (watching rides the separate
    // `watch_tournament_game_*` family, reducers/watch.ts) — so a spawn here
    // is exactly the REST `is_joined` signal `statusInput.ts` was missing
    // after a reload mid-game. Never flip a `false` (fix P: a withdrawn
    // player can still finish a live game).
    joinedLocal:
      sameTournament && state.tournament.joinedLocal === null
        ? true
        : state.tournament.joinedLocal,
  };
  if (tournament.mode !== 'arena') {
    return {tournament};
  }
  bridgeResetGameBerserk();
  bridgeSetArenaStatus({myState: 'paired'});
  const arena: ArenaSlice = {
    ...state.arena,
    // `resetGameBerserk()` — a fresh arena game has no badges yet (L3601).
    myBerserk: false,
    opponentBerserk: false,
    berserkRefusal: null,
    status: mergeStatus(state.arena.status, {myState: 'paired'}),
  };
  return {tournament, arena};
}

/** "5 + 3 • Rated • Blitz" — PlayChess L2394-2401. */
function buildGameDetails(data: any): string {
  let rated = data?.gamePlayMode === 'Play' ? 'Rated' : 'Unrated';
  if (
    String(data?.opponentusername || '').startsWith('Caissa') ||
    data?.startFen !== START_FEN
  ) {
    rated = 'Unrated';
  }
  return `${data.playerMoveTimer / 60000} + ${
    data.moveTimeIncrement / 1000
  } • ${rated} • ${classifyTimeControl(
    data.playerMoveTimer,
    data.moveTimeIncrement,
  )}`;
}

/**
 * game_ready_response — PlayChess L2465-2538.
 * `moveTimer` is SECONDS here (start_game_response.playerMoveTimer is ms):
 * the seconds -> ms conversion happens once, here.
 */
function gameReadyResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  const clockMs = Number(data?.moveTimer || 0) * 1000;
  const whiteId = id(data?.tossWinner);
  const userId = id(data?.userId);
  const opponentUserId = id(data?.opponentuserId);
  // PlayChess L2487 reads `data.opponentUserId` (capital U), which the schema
  // does not define -> black came out undefined whenever userId was white.
  // Fixed here; the seats are the two ids on the message.
  const blackId = userId === whiteId ? opponentUserId : userId;

  // L2479-2483 / L2510-2519 decide "which entry is me" by comparing DISPLAY
  // NAMES (not ids). Replicated literally for parity, quirk included.
  const opponentIsMe = data?.opponentusername === state.session.displayName;
  const opponentName = opponentIsMe ? data?.username : data?.opponentusername;
  const myRating = Number(
    opponentIsMe ? data?.opponentRating : data?.userRating,
  );
  const opponentRating = Number(
    opponentIsMe ? data?.userRating : data?.opponentRating,
  );

  let customRulesMap: any = {};
  try {
    customRulesMap = JSON.parse(data?.customRulesMap || '{}') || {};
  } catch (e) {
    customRulesMap = {};
  }

  const startFen = data?.data || START_FEN;
  const me = state.session.me;

  return {
    // L3746 setWatchGame(false): my own board wins over anything I was watching
    // — a replay included (fix AC, same as `startGameResponse`).
    ...(state.watch.active || state.watch.replayGameId
      ? {...state, watch: {...initialWatch}}
      : state),
    game: {
      ...state.game,
      phase: 'starting',
      tableId: Number(data?.tableId),
      whiteId,
      blackId,
      myColor: me ? (me === whiteId ? 'white' : 'black') : null,
      opponentId: me && me === userId ? opponentUserId : userId,
      opponentName,
      myRating: Number.isFinite(myRating) ? myRating : null,
      opponentRating: Number.isFinite(opponentRating) ? opponentRating : null,
      whiteTimeMs: clockMs,
      blackTimeMs: clockMs,
      clockRunningSince: null,
      moves: [],
      fen: startFen,
      startFen,
      previousFen: null,
      isBlackTurnFirst: startFen.split(' ')[1] === 'b',
      customRulesMap,
      readyAt: now,
      result: null,
      endAckNeeded: false,
      drawOffered: false,
      drawSent: false,
      // L3746 has no gamePlayMode of its own, so the mode the SPAWN set decides:
      // a tournament/arena board never offers Abort (see `abortAllowed`).
      showAbort: abortAllowed(state.game.gamePlayMode),
    },
  };
}

/** start_game — PlayChess L2711-2716 (started + playing). */
function startGame(state: LiveGameState, now: number): LiveGameState {
  bridgeSetPlaying(true);
  if (state.game.phase === 'playing') {
    return state;
  }
  return withGame(state, {
    phase: 'playing',
    clockRunningSince: state.game.clockRunningSince ?? now,
  });
}

/**
 * player_turn_response — PlayChess L2558-2698. ONE message per legal move,
 * delivered to both players.
 */
function playerTurnResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  if (otherTable(state.game, data)) {
    // Tournament/arena players can hold a stale room subscription; PlayChess
    // has no guard here and paints the other board.
    return state;
  }

  const incoming: string[] = Array.isArray(data?.moves) ? data.moves : [];
  // Broadcast-delta (L2568-2575): 'on connect' is the FULL list, the
  // game-start message ('requestid') is [], everything else is a delta.
  let moves: string[];
  if (data?.requestId === 'on connect') {
    moves = incoming.slice();
  } else if (data?.requestId === 'requestid') {
    moves = [];
  } else {
    moves = [...state.game.moves, ...incoming];
  }

  const me = state.session.me;
  const turnUserId = id(data?.userId);
  // L2671-2676: abort is offered only before my own first move — i.e. no move
  // yet, or exactly one move and it is now MY turn (the opponent opened).
  const showAbort =
    abortAllowed(state.game.gamePlayMode) &&
    !(moves.length > 1 || (moves.length === 1 && turnUserId !== me));

  // L2683-2691 thinking-time gate. `my_time` and `total_time` are both in
  // SECONDS and the window is (total_time / 60) seconds -> for rule holders
  // this is effectively always on after move 10. Kept as-is for parity.
  const totalTimeSec = state.game.tcMs / 1000;
  const myTimeSec =
    me && me === state.game.whiteId
      ? Number(data?.whiteTime)
      : Number(data?.blackTime);
  const gateOpen =
    moves.length > 10 && myTimeSec > totalTimeSec / 10 && turnUserId === me;

  // The server aborts the game if EITHER side misses its first move, so the
  // cosmetic countdown belongs to whoever still owes theirs: the side to move in
  // the START POSITION until ply 1, the other side until ply 2 (`firstMove.ts`
  // — a 'b'-to-move custom FEN swaps the two). When the opening move lands and
  // it was not mine, my window is re-armed from the receipt of that move.
  //
  // MIRRORS the old screen: ClockComponent L185-200 shows
  // `remainingClockSeconds - firstMoveTimer` where `firstMoveTimer` is the
  // threshold set at L2328-2335 (`total_time - 20|30`, `total_time / 2`), and
  // the answering side's clock only starts running when the opening move has
  // been played — so the old window effectively starts at that moment too.
  // There is no re-arm there because the value is derived from the clock; ours
  // is a deadline, so it is re-stamped.
  //
  // The re-stamp fires ONLY on the genuine 0 -> 1 transition of a live message:
  // a restore snapshot ('on connect' carries the FULL list, L2568-2575) and a
  // replayed/duplicate ply-1 message must leave the deadline exactly as it is,
  // or a reconnect would hand the player a fresh 20 s every time.
  const firstMovePly = myFirstMovePly(state.game);
  const opensMyWindow =
    firstMovePly === 2 &&
    data?.requestId !== 'on connect' &&
    state.game.moves.length === 0 &&
    moves.length === 1;
  const firstMoveDeadlineMs =
    moves.length >= firstMovePly
      ? null
      : opensMyWindow
      ? now + state.game.firstMoveSec * 1000
      : state.game.firstMoveDeadlineMs;

  return {
    ...withGame(state, {
      phase: 'playing',
      // Defensive seating: a turn message can be the first thing we see on a
      // reconnect where start_game_response was missed. `otherTable` already
      // let it through (tableId was unset), so adopt the table it names —
      // otherwise every later guard stays disabled.
      tableId:
        state.game.tableId ??
        (data?.tableId != null ? Number(data.tableId) : null),
      fen: data?.data,
      previousFen: data?.previousFen ?? null,
      turnUserId,
      moves,
      whiteTimeMs: Number(data?.whiteTime || 0) * 1000,
      blackTimeMs: Number(data?.blackTime || 0) * 1000,
      clockRunningSince: now,
      showAbort,
      // it dies with MY first move, not with the game's (see above)
      firstMoveDeadlineMs,
      // touch_piece_rule: a restriction lives for ONE turn. The old screen
      // clears it the moment it stops being my turn (app L5806-5812
      // `if (!myTurn) setRestrictedPiece('')`); without this the "nothing
      // restricted yet" guard in `actions.touchPiece` would lock the rule out
      // after its first use in a game.
      restrictedPiece: turnUserId === me ? state.game.restrictedPiece : '',
      thinkingGateUntil: gateOpen
        ? now + (totalTimeSec / 60) * 1000
        : state.game.thinkingGateUntil,
      origin: data?.requestId === 'on connect' ? state.game.origin : 'live',
    }),
    // the move (mine or the opponent's) is acknowledged -> watchdog off
    meta: {...state.meta, pendingMoveSentAt: null},
  };
}

/**
 * invalid_move — the server's answer to an ILLEGAL move (out-of-turn moves are
 * dropped silently instead). `data` is the FEN to revert the board to.
 * PlayChess never handled this type.
 */
function invalidMove(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  if (otherTable(state.game, data)) {
    return state;
  }
  return {
    ...withGame(state, {fen: data?.data || state.game.fen}),
    meta: {
      ...state.meta,
      pendingMoveSentAt: null,
      lastInvalidMove: {
        move: data?.move || '',
        message: data?.message || '',
        at: now,
      },
    },
  };
}

/** draw_check — PlayChess L2718-2728: broadcast, `userId` = the offered party. */
function drawCheck(state: LiveGameState, data: any): LiveGameState {
  if (id(data?.userId) !== state.session.me || otherTable(state.game, data)) {
    return state;
  }
  return withGame(state, {drawOffered: true});
}

/** draw_reject — PlayChess L2730-2749. */
function drawReject(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  if (otherTable(state.game, data)) {
    return state;
  }
  // The server BROADCASTS the rejection to both seats. `data.userId` is the
  // player whose offer was declined (`responsee` is the decliner), and the old
  // screens only showed "declined" to that player (web L2737 / app L4205
  // `if (data.userId == username)`). Without this guard the DECLINER saw
  // "opponent declined the draw" after declining (owner bug 2026-10-05).
  const myOfferWasDeclined = id(data?.userId) === state.session.me;
  return withGame(state, {
    drawSent: false,
    drawRejectedAt: myOfferWasDeclined ? now : state.game.drawRejectedAt,
  });
}

/**
 * game_end_response — PlayChess L2870-3089.
 * Re-sent on EVERY fetch/reconnect until `end_game_confirmed`, so this must be
 * idempotent: the same (tableId, endType, winner) only re-arms the ack.
 */
function gameEndResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  if (otherTable(state.game, data)) {
    // PlayChess's own guard is disabled (`data.tableId == tableid || 1`,
    // L3016); V2 honours it so a spectated/stale table cannot end my game.
    return state;
  }

  const key = `${data?.tableId}:${data?.endType}:${data?.winner}`;
  if (state.game.result && state.game.result.key === key) {
    // Duplicate of a result we already showed: re-arm the ack only, so the
    // effects hook re-sends end_game_confirmed without re-opening the modal.
    return withGame(state, {endAckNeeded: true});
  }

  const me = state.session.me;
  const endType = data?.endType;
  const winner = id(data?.winner);
  const iAmWinner = !!me && winner === me;
  const isDraw = DRAW_END_TYPES.includes(endType) || winner === '00';

  // Outcome + copy: the exact branch order and strings of L2956-3015.
  let outcome: GameResult['outcome'];
  let message: string;
  if (isDraw) {
    outcome = 'Draw';
    message = 'The match ended in a draw.';
  } else if (endType === 'abort') {
    outcome = 'Abort';
    message = 'The match was aborted.';
  } else if (iAmWinner) {
    outcome = 'Win';
    message = 'Congratulations! You won the match!';
  } else {
    outcome = 'Loss';
    message = 'Sorry, you lost the match.';
  }

  // Rating deltas are signed FLOAT strings ('0' for unrated/abort/bot).
  // PlayChess parseInt()s them (dropping the fraction) and, on the losing
  // side, shows MY delta as the opponent's too (L3007-3012) — both fixed here.
  const winnerDelta = Number(data?.winner_rating_delta) || 0;
  const opponentDelta = Number(data?.opponent_rating_delta) || 0;
  const aborted = endType === 'abort';

  // Fix X: `clockRunningSince` is nulled below, which used to be the ONLY
  // clock change on an end — `whiteTimeMs`/`blackTimeMs` stayed at their last
  // `player_turn_response` snapshot (the value at TURN START), so a flagged
  // clock visually rewound to what it showed when the turn began (the
  // owner's report). Fold the elapsed time into whichever side was running
  // instead; this is correct for EVERY end type (a resignation 5 s into a
  // turn shows the true remaining, not the turn-start value). The side that
  // was NOT running is left exactly as it was.
  let running: Side | null = null;
  if (state.game.turnUserId && state.game.turnUserId === state.game.whiteId) {
    running = 'white';
  } else if (
    state.game.turnUserId &&
    state.game.turnUserId === state.game.blackId
  ) {
    running = 'black';
  }
  const elapsed = state.game.clockRunningSince
    ? Math.max(0, now - state.game.clockRunningSince)
    : 0;
  let whiteTimeMs = state.game.whiteTimeMs;
  let blackTimeMs = state.game.blackTimeMs;
  if (running) {
    const runningSideMs =
      running === 'white' ? state.game.whiteTimeMs : state.game.blackTimeMs;
    let folded = Math.max(0, runningSideMs - elapsed);
    // 'timeup' IS the flag: never show "0:00.4" because of client clock
    // drift. 'insufficient' folding to under 1 s is the timer's OWN
    // insufficient-material variant (same broadcast, L76-78) — a
    // move-caused insufficient draw leaves the clock nowhere near zero, so
    // this cannot misfire on that case.
    if (endType === 'timeup' || (endType === 'insufficient' && folded < 1000)) {
      folded = 0;
    }
    if (running === 'white') {
      whiteTimeMs = folded;
    } else {
      blackTimeMs = folded;
    }
  }

  const result: GameResult = {
    key,
    endType,
    winner: winner || null,
    isDraw,
    outcome,
    message,
    myRating: iAmWinner
      ? Number(data?.winner_rating)
      : Number(data?.opponent_rating),
    myRatingDelta: aborted ? 0 : iAmWinner ? winnerDelta : opponentDelta,
    opponentRating: iAmWinner
      ? Number(data?.opponent_rating)
      : Number(data?.winner_rating),
    opponentRatingDelta: aborted ? 0 : iAmWinner ? opponentDelta : winnerDelta,
    gameUuid: iAmWinner ? data?.winner_gameId : data?.opponent_gameId,
    opponentGameUuid: iAmWinner ? data?.opponent_gameId : data?.winner_gameId,
    pgn: data?.pgn || '',
    moves: Array.isArray(data?.moves) ? data.moves.slice() : state.game.moves,
    finalFen: data?.gameState || state.game.fen,
    endedAt: now,
    // L3050-3055 (web, 1.5 s) / app L4603-4606 (2 s): checkmate/stalemate hold
    // the modal back so the mating move is visible, for whatever this
    // platform's own delay is (fix E10 — `platform/modalDelay`, not a branch
    // here). The delay is applied by useGameplayEffects, not here.
    modalDelayMs:
      endType === 'checkmate' || endType === 'stalemate' ? MODAL_DELAY_MS : 0,
  };

  bridgeSetPlaying(false);

  return {
    ...withGame(state, {
      phase: 'ended',
      fen: data?.gameState || state.game.fen,
      moves: result.moves,
      clockRunningSince: null,
      whiteTimeMs,
      blackTimeMs,
      drawOffered: false,
      drawSent: false,
      restrictedPiece: '',
      thinkingGateUntil: null,
      firstMoveDeadlineMs: null,
      showAbort: false,
      // L3038-3039: remembered so the lobby can offer the same TC again.
      tcMs: Number(data?.playerMoveTimer) || state.game.tcMs,
      incMs: Number(data?.moveTimeIncrement) || state.game.incMs,
      result,
      endAckNeeded: true,
    }),
    lobby: {...state.lobby, phase: 'idle', seekTableId: null},
    // The game end IS the ack of the final move: when MY move mates (or
    // stalemates, or triggers a repetition) the server answers with this
    // instead of `player_turn_response`, so the watchdog stamp would otherwise
    // survive the game and fire a `fetch_game_state` 3 s into the result card.
    meta: {...state.meta, pendingMoveSentAt: null},
    ...arenaGameEndSlice(state),
  };
}

/**
 * The arena half of a game end — PlayChess L4576-4595.
 *
 * `myBerserk` is CAPTURED before the badges are cleared, because the result card
 * is rendered from the same batch and needs to know whether the game that just
 * ended was berserked by me. `myState` is stamped optimistically too: 'paired'
 * must not linger once the board is gone (it would read "Game starting…" until
 * the status reply lands), and the server re-pools both players at scoring
 * unless they are paused.
 *
 * The `arena_status_request` the old screen fires here is a SEND, so it belongs
 * to the screen root's effect, not to this reducer.
 */
function arenaGameEndSlice(state: LiveGameState): Partial<LiveGameState> {
  if (state.tournament.mode !== 'arena') {
    return {};
  }
  bridgeResetGameBerserk();
  bridgeSetArenaStatus({
    myState: state.arena.status?.paused ? 'paused' : 'pooled',
  });
  return {
    arena: {
      ...state.arena,
      lastGameBerserk: state.arena.myBerserk,
      myBerserk: false,
      opponentBerserk: false,
      status: mergeStatus(state.arena.status, {
        myState: state.arena.status?.paused ? 'paused' : 'pooled',
      }),
    },
  };
}

/**
 * no_ongoing_game — PlayChess L1862-1874 resets the board unconditionally.
 *
 * DELIBERATE DEVIATION: only phase 'none' is resettable. The server keeps the
 * seat when we are queued in matchmaking and answers a fetch with this message,
 * so a fetch fired mid-game (reconnect, screen remount, move-ack watchdog)
 * would otherwise wipe a live board.
 *
 * 'ended' is ignored too (P0-1): the result card is AUTHORITATIVE until the
 * user acts on it (`resetForNewGame` / rematch / New game / Go to tournament).
 * The old screen was immune for free because its result modal was an
 * independent flag this handler never touched (L1862-1874); here the result
 * lives in the same slice, so any late `no_ongoing_game` — the move-ack
 * watchdog after a game MY move ended, an app foreground re-sync, a reconnect
 * fetch — used to delete `game.result` and drop the rail back to 'home'.
 */
function noOngoingGame(state: LiveGameState): LiveGameState {
  if (state.game.phase !== 'none') {
    return state;
  }
  bridgeSetPlaying(false);
  return {
    ...state,
    game: {...initialGame},
    lobby: {...state.lobby, phase: 'idle'},
  };
}

/** tournament_opening_prep_eval_pending_response — PlayChess L2848-2864. */
function evalPending(state: LiveGameState, data: any): LiveGameState {
  if (otherTable(state.game, data)) {
    return state;
  }
  bridgeSetPlaying(false);
  return withGame(state, {
    phase: 'evalPending',
    clockRunningSince: null,
    drawOffered: false,
    drawSent: false,
  });
}
